/**
 * A community room's roster, as the room reads it (ADR 0043).
 *
 * Two lists on different clocks. The ROSTER is who belongs to this room —
 * edited rarely, by a person, or fetched from a feed. The LIVE SET is who is
 * streaming right now, which changes all evening. A room shows only the
 * second, and a roster of fifty people is normal because nobody watches fifty
 * streams.
 */
import { z } from 'zod';
import { RoomId, TwitchLogin } from './room.js';
import { Grouping, GroupingId } from './grouping.js';

export const LiveState = z.object({
  title: z.string(),
  game_name: z.string(),
  viewer_count: z.number().int().min(0),
  thumbnail_url: z.string().url().nullable(),
  /** Used to order the grid by uptime, so a missing one is meaningful. */
  started_at: z.string().datetime().nullable(),
});

/**
 * One person on a roster.
 *
 * The array is ORDERED: live first, longest uptime first, then everyone else
 * alphabetically. Order is part of the answer rather than a detail the client
 * re-derives — a room and its picker should agree about who is at the top,
 * and `apps/` may not import `@tomo/domain`, so a client deciding for itself
 * would be a second copy of the rule with no way to check it against the
 * first.
 */
export const RosterMember = z.object({
  login: TwitchLogin,
  display_name: z.string(),
  avatar_url: z.string().url().nullable(),
  description: z.string(),
  tags: z.array(z.string()).max(8),
  /** Null when they are not streaming. The room shows only those who are. */
  live: LiveState.nullable(),
  /**
   * The community's groupings this person is in, in the community's order
   * (ADR 0052). A child's parent is included, so a filter on "Speedrun"
   * finds everybody in "Speedrun · Vanilla" without knowing the tree.
   */
  groupings: z.array(GroupingId).default([]),
});

/** The wire shape, for consumers that hold one member rather than a list. */
export type RosterMemberShape = z.infer<typeof RosterMember>;

export const RoomRoster = z.object({
  data: z.array(RosterMember),
  /**
   * Where this came from, when it came from somewhere else.
   *
   * Present so the room can credit the source — we are re-publishing
   * somebody's list, and a room that showed their work with no attribution
   * would be taking it.
   */
  source: z
    .object({ id: z.string(), label: z.string(), homepage: z.string().url() })
    .nullable(),
  /** ISO. Null when the feed has never been read successfully. */
  refreshed_at: z.string().datetime().nullable(),
  /**
   * True when the feed last failed and this is the previous good answer.
   *
   * Published rather than hidden: a stale roster shown as current is a lie,
   * and an empty one is indistinguishable from a quiet evening.
   */
  stale: z.boolean(),
  /**
   * The community's groupings that somebody on this roster is in, in the
   * community's order. An empty grouping is left out: a heading or a filter
   * that finds nobody reads as a community with nobody in it.
   */
  groupings: z.array(Grouping).default([]),
});

export type LiveState = z.infer<typeof LiveState>;
export type RosterMember = z.infer<typeof RosterMember>;
export type RoomRoster = z.infer<typeof RoomRoster>;

/**
 * How many of a community's roster are live, for a card that has no room for
 * the roster itself (ADR 0051).
 *
 * `live` is NULL when the sweep has no current reading for the room — its
 * source failed, the sweep is behind, or the room is not a community. Null is
 * "do not say", never zero: a card reading "0 live" during an outage would
 * tell everybody the community had gone quiet.
 */
export const RoomLiveCount = z.object({
  room_id: RoomId,
  live: z.number().int().nonnegative().nullable(),
  /** Up to eight of them, largest audience first. */
  logins: z.array(TwitchLogin),
});
export type RoomLiveCount = z.infer<typeof RoomLiveCount>;

export const RoomLiveCounts = z.object({
  data: z.array(RoomLiveCount),
  /** ISO. When the counts were taken; null when there is no current reading. */
  swept_at: z.string().datetime().nullable(),
});
export type RoomLiveCounts = z.infer<typeof RoomLiveCounts>;
